/**
 * ServiceContractEditor — Batch #14.240
 *
 * Éditeur du contenu d'un contrat de services : titre, bloc client, sections
 * (textes/prix 100 % modifiables), cases à cocher pour le client, ville de signature.
 * Batch #14.241 — Modèles de contrat : sauvegarder / réutiliser / supprimer.
 * Composant contrôlé : value / onChange.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, FolderOpen } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


const inputCls = "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm";

export function ServiceContractEditor({ value, onChange }) {
  const v = value;
  const set = (patch) => onChange({ ...v, ...patch });
  const setSection = (i, patch) =>
    set({ sections: v.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const moveSection = (i, dir) => {
    const arr = [...v.sections];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set({ sections: arr });
  };

  // ── Modèles de contrat (Batch #14.241) ──
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [tplName, setTplName] = useState("");

  const loadTemplates = () => {
    axios.get(`${API}/admin/contract-templates`, { headers: authH() })
      .then((r) => setTemplates(r.data?.items || []))
      .catch(() => {});
  };
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (id) => {
    setSelectedTpl(id);
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    set({
      title: tpl.title,
      sections: tpl.sections,
      checkboxes: tpl.checkboxes || [],
      sign_city: tpl.sign_city || "Saguenay",
    });
    setTplName(tpl.name);
    toast.success(`Modèle « ${tpl.name} » appliqué — le client et ses coordonnées restent à choisir`);
  };

  const saveTemplate = async () => {
    const name = tplName.trim();
    if (!name) { toast.error("Donnez un nom au modèle (ex : Contrat tenue de livres)"); return; }
    try {
      const r = await axios.post(`${API}/admin/contract-templates`, {
        name,
        title: v.title,
        sections: v.sections,
        checkboxes: v.checkboxes.filter((c) => c.trim()),
        sign_city: v.sign_city,
      }, { headers: authH() });
      toast.success(r.data.updated ? `Modèle « ${name} » mis à jour` : `Modèle « ${name} » sauvegardé`);
      loadTemplates();
      setSelectedTpl(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la sauvegarde du modèle");
    }
  };

  const deleteTemplate = async () => {
    const tpl = templates.find((t) => t.id === selectedTpl);
    if (!tpl) return;
    if (!window.confirm(`Supprimer le modèle « ${tpl.name} » ? (les contrats déjà envoyés ne sont pas touchés)`)) return;
    try {
      await axios.delete(`${API}/admin/contract-templates/${tpl.id}`, { headers: authH() });
      toast.message("Modèle supprimé");
      setSelectedTpl("");
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-sky-50/50 border border-sky-200 rounded-xl p-3 space-y-3" data-testid="contract-editor">
      {/* Modèles sauvegardés */}
      <div className="bg-white border border-slate-200 rounded-xl p-2.5 space-y-2">
        <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
          <FolderOpen className="w-3.5 h-3.5 text-sky-700" /> Mes modèles de contrat
        </p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select value={selectedTpl} onChange={(e) => applyTemplate(e.target.value)}
            className="flex-1 min-w-[180px] border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm"
            data-testid="contract-template-select">
            <option value="">— Partir d'un modèle sauvegardé —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {selectedTpl && (
            <button type="button" onClick={deleteTemplate}
              className="text-slate-300 hover:text-red-500 p-1.5" title="Supprimer ce modèle"
              data-testid="contract-template-delete">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <input value={tplName} onChange={(e) => setTplName(e.target.value)}
            placeholder="Nom du modèle (ex : Contrat tenue de livres)"
            className="flex-1 border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm"
            data-testid="contract-template-name" />
          <button type="button" onClick={saveTemplate}
            className="px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold inline-flex items-center gap-1.5 flex-shrink-0"
            data-testid="contract-template-save">
            <Save className="w-3.5 h-3.5" /> Sauvegarder comme modèle
          </button>
        </div>
        <p className="text-[10px] text-slate-400">
          Sauvegarde le contenu actuel (titre, sections, prix, cases) — même nom = mise à jour du modèle. Le client n'est jamais inclus dans le modèle.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Titre du contrat</label>
          <input value={v.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} data-testid="contract-title" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Signé à (ville)</label>
          <input value={v.sign_city} onChange={(e) => set({ sign_city: e.target.value })} className={inputCls} data-testid="contract-sign-city" />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Entreprise du client (page couverture — facultatif)</label>
        <input value={v.client_company} onChange={(e) => set({ client_company: e.target.value })} className={inputCls} data-testid="contract-client-company" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">
          Bloc « ET : » (coordonnées du client — une info par ligne, pré-rempli selon le client, modifiable)
        </label>
        <textarea rows={4} value={v.client_block} onChange={(e) => set({ client_block: e.target.value })}
          className={inputCls} data-testid="contract-client-block" />
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-slate-700">Sections du contrat (tout est modifiable — textes, prix, puces avec « • ») :</p>
        {v.sections.map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-2.5 space-y-1.5" data-testid={`contract-section-${i}`}>
            <div className="flex items-center gap-1.5">
              <input value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })}
                placeholder="Titre de la section (ex : 1. OBJET DU CONTRAT)"
                className={`${inputCls} font-semibold`} data-testid={`contract-section-heading-${i}`} />
              <button type="button" onClick={() => moveSection(i, -1)} className="text-slate-400 hover:text-slate-700 p-1" title="Monter">
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => moveSection(i, 1)} className="text-slate-400 hover:text-slate-700 p-1" title="Descendre">
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => set({ sections: v.sections.filter((_, j) => j !== i) })}
                className="text-slate-300 hover:text-red-500 p-1" title="Supprimer la section"
                data-testid={`contract-section-delete-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea rows={4} value={s.body} onChange={(e) => setSection(i, { body: e.target.value })}
              placeholder={"Texte de la section. Commencez une ligne par « • » pour une puce. **texte** = gras."}
              className={inputCls} data-testid={`contract-section-body-${i}`} />
          </div>
        ))}
        <button type="button" onClick={() => set({ sections: [...v.sections, { heading: "", body: "" }] })}
          className="w-full text-sm text-slate-600 border border-dashed border-slate-300 rounded-xl px-3 py-2 hover:bg-white inline-flex items-center justify-center gap-1.5"
          data-testid="contract-add-section">
          <Plus className="w-4 h-4" /> Ajouter une section
        </button>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-slate-700">Cases à cocher pour le client (facultatif — il les cochera avant de signer) :</p>
        {v.checkboxes.map((label, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input value={label} onChange={(e) => set({ checkboxes: v.checkboxes.map((c, j) => (j === i ? e.target.value : c)) })}
              className={inputCls} data-testid={`contract-checkbox-label-${i}`} />
            <button type="button" onClick={() => set({ checkboxes: v.checkboxes.filter((_, j) => j !== i) })}
              className="text-slate-300 hover:text-red-500 p-1" data-testid={`contract-checkbox-delete-${i}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => set({ checkboxes: [...v.checkboxes, ""] })}
          className="text-xs text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-1.5 hover:bg-white inline-flex items-center gap-1"
          data-testid="contract-add-checkbox">
          <Plus className="w-3.5 h-3.5" /> Ajouter une case à cocher
        </button>
      </div>
    </div>
  );
}
